// Screen 1: Landing page with search, popular questions, and category cards
// Design matches sample: grouped list cards, 2-col grid, icon badges

import { useState, useMemo, useRef } from 'react';
import { Box, Typography } from '@/ui';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import KeyIcon from '@mui/icons-material/Key';
import ShareIcon from '@mui/icons-material/Share';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentsIcon from '@mui/icons-material/Payments';
import InfoIcon from '@mui/icons-material/Info';
import LockIcon from '@mui/icons-material/Lock';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  CATEGORIES,
  SEARCH_CONFIG,
  getHomeFaqs,
  searchFaqs,
  getCategoryById,
} from '../data/supportFaqData';
import type { SupportCategory, SupportFaq } from '../data/supportFaqData';

interface SupportLandingProps {
  onSelectCategory: (categoryId: string) => void;
  onSelectFaq: (faqId: string) => void;
}

const COLORS = {
  accent: '#004b49',
  accentBg: '#e7f5f4',
  accentBgHover: '#d3ece9',
  borderAccent: '#b8e3df',
  bgCard: '#ffffff',
  border: '#e5e7eb',
  borderSubtle: '#f3f4f6',
  text: '#111827',
  textSecondary: '#4b5563',
  textMuted: '#9ca3af',
  bgInput: '#ffffff',
  shadow: '0 1px 2px 0 rgba(0,0,0,0.04)',
  shadowLg: '0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.06)',
};

const CATEGORY_ICON_MAP: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
  'SEC-LOGIN': (p) => <KeyIcon sx={{ fontSize: p.size ?? 18, color: p.color }} />,
  'SEC-SHARING': (p) => <ShareIcon sx={{ fontSize: p.size ?? 18, color: p.color }} />,
  'SEC-BUDGET': (p) => <EditNoteIcon sx={{ fontSize: p.size ?? 18, color: p.color }} />,
  'SEC-DOC': (p) => <DescriptionIcon sx={{ fontSize: p.size ?? 18, color: p.color }} />,
  'SEC-DOCUMENTS': (p) => <DescriptionIcon sx={{ fontSize: p.size ?? 18, color: p.color }} />,
  'SEC-PAYMENTS': (p) => <PaymentsIcon sx={{ fontSize: p.size ?? 18, color: p.color }} />,
  'SEC-ABOUT': (p) => <InfoIcon sx={{ fontSize: p.size ?? 18, color: p.color }} />,
  'SEC-PRIVACY': (p) => <LockIcon sx={{ fontSize: p.size ?? 18, color: p.color }} />,
};

function SectionHeader({ title }: { title: string }) {
  return (
    <Box data-testid="features-support-faqs-components-section-header-container-action-1" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Typography data-testid="features-support-faqs-components-section-header-text-action"
        variant="h6"
        sx={{ fontWeight: 600, fontSize: '15px', color: COLORS.text, fontFamily: 'Inter, sans-serif' }}
      >
        {title}
      </Typography>
      <Box data-testid="features-support-faqs-components-section-header-container-action-2" sx={{ flex: 1, height: '1px', backgroundColor: COLORS.border }} />
    </Box>
  );
}

function SupportLanding({ onSelectCategory, onSelectFaq }: SupportLandingProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const homeFaqs = useMemo(() => getHomeFaqs(), []);
  const searchResults = useMemo(() => searchFaqs(searchQuery), [searchQuery]);
  const isSearching = searchQuery.trim().length > 0;
  const showSearchResults = isSearching && searchResults.length > 0;
  const showNoResults = isSearching && searchResults.length === 0;

  // Popular links (first 4 home FAQs for quick text links)
  const popularLinks = homeFaqs.slice(0, 4);

  return (
    <Box data-testid="features-support-faqs-components-support-landing-container-action-1" sx={{ fontFamily: 'Inter, sans-serif', maxWidth: '100%', overflowX: 'clip' }}>
      {/* Header */}
      <Box data-testid="features-support-faqs-components-support-landing-container-action-2" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box data-testid="features-support-faqs-components-support-landing-container-action-3"
          sx={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: '12px',
            backgroundColor: COLORS.accentBg,
            border: `1px solid ${COLORS.borderAccent}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SupportAgentIcon sx={{ fontSize: 22, color: COLORS.accent }} />
        </Box>
        <Box data-testid="features-support-faqs-components-support-landing-container-action-4">
          <Typography data-testid="features-support-faqs-components-support-landing-text-support-and-faqs"
            sx={{
              fontSize: { xs: '22px', md: '24px' },
              fontWeight: 700,
              color: COLORS.text,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Support and FAQ's
          </Typography>
          <Typography data-testid="features-support-faqs-components-support-landing-text-how-can-we-help-you-today?"
            sx={{
              fontSize: '14px',
              color: COLORS.textSecondary,
              mt: 0.25,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            How can we help you today?
          </Typography>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box data-testid="features-support-faqs-components-support-landing-container-action-5" sx={{ position: 'relative', mb: 2.5 }}>
        <Box data-testid="features-support-faqs-components-support-landing-container-action-6"
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <SearchIcon
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: COLORS.textMuted,
              fontSize: 20,
              pointerEvents: 'none',
            }}
          />
          <Box data-testid="features-support-faqs-components-support-landing-container-action-7"
            component="input"
            ref={inputRef}
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={SEARCH_CONFIG.placeholderText}
            sx={{
              width: '100%',
              pl: 6,
              pr: 6,
              py: 2,
              borderRadius: '16px',
              fontSize: '15px',
              outline: 'none',
              backgroundColor: COLORS.bgInput,
              color: COLORS.text,
              border: isFocused ? `2px solid ${COLORS.accent}` : `1px solid ${COLORS.border}`,
              boxShadow: isFocused ? `0 0 0 3px ${COLORS.accentBg}` : COLORS.shadow,
              fontFamily: 'Inter, sans-serif',
              '&::placeholder': { color: COLORS.textMuted },
            }}
          />
          {searchQuery && (
            <Box data-testid="features-support-faqs-components-support-landing-container-action-8"
              component="button"
              onClick={() => {
                setSearchQuery('');
                inputRef.current?.focus();
              }}
              sx={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: 'none',
                backgroundColor: '#f3f4f6',
                color: COLORS.textMuted,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </Box>
          )}
        </Box>

        {/* Search Results Dropdown */}
        {showSearchResults && (
          <Box data-testid="features-support-faqs-components-support-landing-container-action-9"
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 1,
              borderRadius: '16px',
              overflow: 'hidden',
              backgroundColor: COLORS.bgCard,
              border: `1px solid ${COLORS.border}`,
              boxShadow: COLORS.shadowLg,
              maxHeight: '400px',
              overflowY: 'auto',
              zIndex: 20,
            }}
          >
            {searchResults.map((faq: SupportFaq, i) => {
              const cat = getCategoryById(faq.categoryId);
              return (
                <Box data-testid="features-support-faqs-components-support-landing-container-action-10"
                  key={faq.id}
                  component="button"
                  onClick={() => onSelectFaq(faq.id)}
                  sx={{
                    width: '100%',
                    px: 2,
                    py: 1.75,
                    textAlign: 'left',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    borderBottom: i < searchResults.length - 1 ? `1px solid ${COLORS.borderSubtle}` : 'none',
                    '&:hover': { backgroundColor: COLORS.accentBg },
                  }}
                >
                  <Typography data-testid="features-support-faqs-components-support-landing-text-action-1"
                    sx={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, mb: 0.25, fontFamily: 'Inter, sans-serif' }}
                  >
                    {faq.title}
                  </Typography>
                  {cat && (
                    <Typography data-testid="features-support-faqs-components-support-landing-text-action-2" sx={{ fontSize: '12px', color: COLORS.textMuted, fontFamily: 'Inter, sans-serif' }}>
                      {cat.title}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {showNoResults && (
          <Box data-testid="features-support-faqs-components-support-landing-container-action-11"
            sx={{
              mt: 1.5,
              p: 2.5,
              borderRadius: '16px',
              textAlign: 'center',
              backgroundColor: COLORS.bgCard,
              border: `1px solid ${COLORS.border}`,
              boxShadow: COLORS.shadow,
            }}
          >
            <Typography data-testid="features-support-faqs-components-support-landing-text-action-3" sx={{ fontSize: '13px', color: COLORS.textSecondary, fontFamily: 'Inter, sans-serif' }}>
              {SEARCH_CONFIG.noResultsMessage}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Popular links */}
      {!isSearching && (
        <Box data-testid="features-support-faqs-components-support-landing-container-action-12" sx={{ mb: 5, maxWidth: '100%' }}>
          <Typography data-testid="features-support-faqs-components-support-landing-text-popular:"
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: COLORS.textMuted,
              mb: 1,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Popular:
          </Typography>
          <Box data-testid="features-support-faqs-components-support-landing-container-action-13"
            sx={{
              display: 'flex',
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              gap: 1,
              maxWidth: '100%',
              overflowX: { xs: 'auto', sm: 'visible' },
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              // allow scroll past the last chip edge for comfort
              pb: { xs: 0.5, sm: 0 },
            }}
          >
            {popularLinks.map((faq) => (
              <Box data-testid="features-support-faqs-components-support-landing-container-action-14"
                key={faq.id}
                component="button"
                onClick={() => onSelectFaq(faq.id)}
                sx={{
                  fontSize: '12.5px',
                  fontWeight: 500,
                  color: COLORS.accent,
                  backgroundColor: COLORS.accentBg,
                  border: `1px solid ${COLORS.borderAccent}`,
                  borderRadius: '999px',
                  cursor: 'pointer',
                  px: 1.5,
                  py: 0.625,
                  fontFamily: 'Inter, sans-serif',
                  transition: 'all 0.15s',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  lineHeight: 1.3,
                  '&:hover': {
                    backgroundColor: COLORS.accentBgHover,
                    borderColor: COLORS.accent,
                  },
                  '&:active': { transform: 'scale(0.97)' },
                }}
              >
                {faq.title.replace(/\?$/, '')}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Common Questions */}
      {!isSearching && (
        <Box data-testid="features-support-faqs-components-support-landing-container-action-15" sx={{ mb: 5 }}>
          <SectionHeader title="Common questions" />

          <Box data-testid="features-support-faqs-components-support-landing-container-action-16"
            sx={{
              borderRadius: '16px',
              overflow: 'hidden',
              border: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.bgCard,
              boxShadow: COLORS.shadow,
            }}
          >
            {homeFaqs.map((faq, i) => (
              <Box data-testid="features-support-faqs-components-support-landing-container-action-17"
                key={faq.id}
                component="button"
                onClick={() => onSelectFaq(faq.id)}
                sx={{
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  px: { xs: 1.75, sm: 2.5 },
                  py: 2,
                  textAlign: 'left',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  borderBottom: i < homeFaqs.length - 1 ? `1px solid ${COLORS.borderSubtle}` : 'none',
                  transition: 'background-color 0.15s',
                  '&:hover': { backgroundColor: COLORS.accentBg },
                }}
              >
                <Box data-testid="features-support-faqs-components-support-landing-container-action-18"
                  sx={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    backgroundColor: COLORS.accentBg,
                    border: `1px solid ${COLORS.borderAccent}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 17, color: COLORS.accent }} />
                </Box>
                <Typography data-testid="features-support-faqs-components-support-landing-text-action-4"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '14px',
                    fontWeight: 500,
                    color: COLORS.text,
                    lineHeight: 1.4,
                    fontFamily: 'Inter, sans-serif',
                    wordBreak: 'break-word',
                  }}
                >
                  {faq.title}
                </Typography>
                <ChevronRightIcon sx={{ fontSize: 16, color: COLORS.textMuted, flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Browse all topics — always visible when not searching, and also on no-results so users can fall back to categories */}
      {(!isSearching || showNoResults) && (
        <Box data-testid="features-support-faqs-components-support-landing-container-action-19">
          <SectionHeader title="Browse all topics" />

          <Box data-testid="features-support-faqs-components-support-landing-container-action-20"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            {CATEGORIES
              .slice()
              .sort((a: SupportCategory, b: SupportCategory) => a.displayOrder - b.displayOrder)
              .map((cat: SupportCategory) => {
                const IconComp = CATEGORY_ICON_MAP[cat.id];
                return (
                  <Box data-testid="features-support-faqs-components-support-landing-container-action-21"
                    key={cat.id}
                    component="button"
                    onClick={() => onSelectCategory(cat.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 1.25, sm: 1.75 },
                      px: { xs: 1.5, sm: 2 },
                      py: 2,
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      borderRadius: '16px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      backgroundColor: COLORS.bgCard,
                      border: `1px solid ${COLORS.border}`,
                      boxShadow: COLORS.shadow,
                      transition: 'all 0.15s',
                      '&:hover': {
                        backgroundColor: COLORS.accentBg,
                        borderColor: COLORS.borderAccent,
                      },
                    }}
                  >
                    <Box data-testid="features-support-faqs-components-support-landing-container-action-22"
                      sx={{
                        flexShrink: 0,
                        p: 1.25,
                        borderRadius: '12px',
                        backgroundColor: COLORS.accentBg,
                        border: `1px solid ${COLORS.borderAccent}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {IconComp ? <IconComp size={18} color={COLORS.accent} /> : <InfoIcon sx={{ fontSize: 18, color: COLORS.accent }} />}
                    </Box>
                    <Box data-testid="features-support-faqs-components-support-landing-container-action-23" sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <Typography data-testid="features-support-faqs-components-support-landing-text-action-5"
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: COLORS.text,
                          lineHeight: 1.2,
                          mb: 0.25,
                          fontFamily: 'Inter, sans-serif',
                          wordBreak: 'break-word',
                        }}
                      >
                        {cat.title}
                      </Typography>
                      <Typography data-testid="features-support-faqs-components-support-landing-text-action-6"
                        sx={{
                          fontSize: '12px',
                          color: COLORS.textSecondary,
                          lineHeight: 1.35,
                          fontFamily: 'Inter, sans-serif',
                          wordBreak: 'break-word',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {cat.subtitle}
                      </Typography>
                    </Box>
                    <ChevronRightIcon sx={{ fontSize: 15, color: COLORS.textMuted, flexShrink: 0 }} />
                  </Box>
                );
              })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default SupportLanding;
